import { useEffect, useState, useCallback, useMemo } from "react";
import { Loader2, Search, UserPlus, FileSpreadsheet, Dog, Cat, ChevronRight, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SurveyForm } from "@/components/closefriends/SurveyForm";
import { ESTADOS, estadoInfo, interesLabel } from "@/components/closefriends/constants";
import { CloseFriendDetail } from "./CloseFriendDetail";
import { CloseFriendsFoods } from "./CloseFriendsFoods";
import { CloseFriendsStats } from "./CloseFriendsStats";

const VIEWS = [
  { id: "contacts", label: "Contactos" },
  { id: "foods", label: "Por alimento" },
  { id: "stats", label: "Resumen" },
];

export const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("es-CR", { dateStyle: "medium" }); } catch { return iso; } };

export const CloseFriendsManager = () => {
  const [view, setView] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("");
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [version, setVersion] = useState(0);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/close-friends"); setContacts(data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, version]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return contacts.filter((c) => (!estado || c.estado === estado) &&
      (!t || [c.nombre, c.telefono, c.zona, ...(c.marcas || [])].join(" ").toLowerCase().includes(t)));
  }, [contacts, q, estado]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/close-friends/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `close_friends_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo exportar"); } finally { setExporting(false); }
  };

  const addManual = async (payload) => {
    const { data } = await api.post("/admin/close-friends", payload);
    toast.success(data.actualizado ? "Contacto existente actualizado" : "Contacto agregado");
    setAdding(false); setVersion((v) => v + 1);
  };

  const copyLink = () => { navigator.clipboard?.writeText(`${window.location.origin}/close-friends`); toast.success("Enlace de la encuesta copiado"); };

  return (
    <div className="space-y-4" data-testid="cf-admin">
      <div className="flex flex-wrap items-center gap-2">
        {VIEWS.map((v) => (
          <button key={v.id} data-testid={`cf-view-${v.id}`} onClick={() => setView(v.id)}
            className={`px-4 py-2 rounded-2xl text-sm font-semibold border ${view === v.id ? "bg-coral text-white border-coral" : "bg-white text-charcoal border-[#E3E7E2]"}`}>
            {v.label}{v.id === "contacts" ? ` (${contacts.length})` : ""}
          </button>
        ))}
        <div className="flex-1" />
        <button data-testid="cf-copy-link" onClick={copyLink} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-[#E3E7E2] text-sm font-semibold text-charcoal hover:bg-peach/40"><Copy className="w-4 h-4" /> Copiar enlace</button>
        <button data-testid="cf-add-manual" onClick={() => setAdding(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-[#E3E7E2] text-sm font-semibold text-charcoal hover:bg-peach/40"><UserPlus className="w-4 h-4" /> Agregar</button>
        <button data-testid="cf-export" onClick={exportXlsx} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-60">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Exportar Excel
        </button>
      </div>

      {view === "contacts" && (
        <>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-moss" />
              <input data-testid="cf-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, teléfono, zona o marca"
                className="w-full h-11 pl-9 pr-3 rounded-xl bg-white border border-[#E3E7E2] text-sm focus:outline-none focus:ring-2 focus:ring-coral/40" />
            </div>
            <select data-testid="cf-filter-estado" value={estado} onChange={(e) => setEstado(e.target.value)} className="h-11 px-3 rounded-xl bg-white border border-[#E3E7E2] text-sm">
              <option value="">Todos los estados</option>
              {ESTADOS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
          </div>
          {loading ? (
            <div className="grid place-items-center py-16"><Loader2 className="w-7 h-7 animate-spin text-coral" /></div>
          ) : filtered.length === 0 ? (
            <p data-testid="cf-empty" className="text-center text-muted-moss py-12">Aún no hay contactos. Comparte el enlace de la encuesta con tus Close Friends.</p>
          ) : (
            <div className="space-y-2">{filtered.map((c) => <ContactRow key={c.id} c={c} onOpen={() => setOpenId(c.id)} />)}</div>
          )}
        </>
      )}
      {view === "foods" && <CloseFriendsFoods key={version} onOpen={setOpenId} />}
      {view === "stats" && <CloseFriendsStats key={version} />}

      <CloseFriendDetail id={openId} onClose={() => setOpenId(null)} onSaved={() => setVersion((v) => v + 1)} />

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-cream">
          <DialogHeader><DialogTitle>Agregar contacto manualmente</DialogTitle></DialogHeader>
          <SurveyForm onSubmit={addManual} submitLabel="Guardar contacto" />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ContactRow = ({ c, onOpen }) => {
  const st = estadoInfo(c.estado);
  return (
    <button data-testid={`cf-contact-${c.id}`} onClick={onOpen} className="w-full text-left rounded-2xl bg-white border border-[#E3E7E2] p-4 flex items-center gap-3 hover:border-coral/50 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-bold text-charcoal truncate">{c.nombre}</p>
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${st.cls}`}>{st.label}</span>
          {c.origen === "manual" && <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-cream-2 text-muted-moss">Manual</span>}
        </div>
        <p className="text-xs text-muted-moss mt-0.5">{c.telefono} · {c.zona} · Prueba: {interesLabel(c.interes_prueba)}</p>
        <p className="text-xs text-charcoal mt-1 flex items-center gap-2 flex-wrap">
          {c.tipos.includes("perro") && <Dog className="w-3.5 h-3.5 text-coral" />}
          {c.tipos.includes("gato") && <Cat className="w-3.5 h-3.5 text-coral" />}
          {c.total_mascotas} mascota{c.total_mascotas !== 1 ? "s" : ""} · {c.marcas.join(", ")}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-[11px] text-muted-moss">{fmtDate(c.fecha_actualizacion)}</p>
        <ChevronRight className="w-4 h-4 text-muted-moss ml-auto mt-1" />
      </div>
    </button>
  );
};
